import React, { useCallback, useState } from 'react';

import type { ControllerRenderProps } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import { z } from 'zod';

import DialogModal from 'components/DialogModal/DialogModal';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from 'components/ui/form';

import {
  ContentSelectionMode,
  EContentType,
  IBlockContentProps
} from '../../../../constants';
import {
  contentsPreviewBlockSchema,
  pageFormSchema
} from '../../../../validation-schema';

type ContentsPreviewBlockType = z.infer<typeof contentsPreviewBlockSchema>;
type PageFormType = z.infer<typeof pageFormSchema>;

type ContentsPreviewFieldProps<T extends keyof ContentsPreviewBlockType> =
  ControllerRenderProps<PageFormType, any> & {
    value: ContentsPreviewBlockType[T];
  };

const CONTENT_TYPE_OPTIONS = [
  { label: 'News', value: EContentType.NEWS },
  { label: 'Article', value: EContentType.ARTICLE }
];

interface ContentTypeSelectProps extends Pick<IBlockContentProps, 'blockIndex'> {
  currentContentType: EContentType;
  subType: string;
  selectedContentsIds: string[];
}

export const ContentTypeSelect: React.FC<ContentTypeSelectProps> = ({
  blockIndex,
  currentContentType,
  subType,
  selectedContentsIds
}) => {
  const { control, setValue } = useFormContext<z.infer<typeof pageFormSchema>>();
  const basePath = `blocksV2.${blockIndex}` as const;

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingContentType, setPendingContentType] = useState<
    EContentType | null
  >(null);

  const handleContentTypeChange = useCallback(
    (newContentType: EContentType, onChange: (value: EContentType) => void) => {
      const hasSelectionToClear =
        subType === ContentSelectionMode.USER_SELECTED_CONTENTS &&
        selectedContentsIds.length > 0;

      if (hasSelectionToClear) {
        setPendingContentType(newContentType);
        setIsConfirmOpen(true);
      } else {
        onChange(newContentType);
        setValue(`${basePath}.selectedContentsIds`, [], { shouldValidate: false });
      }
    },
    [basePath, subType, selectedContentsIds.length, setValue]
  );

  const handleConfirm = useCallback(() => {
    if (pendingContentType !== null) {
      setValue(`${basePath}.contentType`, pendingContentType);
      setValue(`${basePath}.selectedContentsIds`, [], { shouldValidate: false });
      setPendingContentType(null);
    }
    setIsConfirmOpen(false);
  }, [basePath, pendingContentType, setValue]);

  const handleCancel = useCallback(() => {
    setPendingContentType(null);
    setIsConfirmOpen(false);
  }, []);

  return (
    <>
      <FormField
        control={control}
        name={`${basePath}.contentType`}
        render={({
          field: { onChange }
        }: {
          field: ContentsPreviewFieldProps<'contentType'>;
        }) => (
          <FormItem>
            <FormLabel>* Content Type</FormLabel>
            <FormControl>
              <InputSelectDropdown
                label=""
                defaultSelected={
                  CONTENT_TYPE_OPTIONS.find(
                    (opt) =>
                      opt.value === (currentContentType || EContentType.NEWS)
                  ) || CONTENT_TYPE_OPTIONS[0]
                }
                options={CONTENT_TYPE_OPTIONS}
                onOptionChange={(value) =>
                  handleContentTypeChange(value as EContentType, onChange)
                }
                iconClassName="text-xs text-base-mutedForeground"
                placeholder="Select content type"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {isConfirmOpen && (
        <DialogModal
          isOpen={isConfirmOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title="Change Content Type"
          description="Changing Content Type will clear your current selection. Continue?"
          modalActions={{
            onConfirm: { label: 'Continue', action: handleConfirm },
            onClose: {
              label: 'Cancel',
              action: handleCancel,
              variant: 'link'
            }
          }}
        />
      )}
    </>
  );
};
